import { useEffect, useState } from 'react';

// Module-level cache so the (large) icon set is only ever fetched once per session,
// no matter how many components/mounts request it.
const iconSetCache = {};

function fetchIconSet(prefix) {
  if (!iconSetCache[prefix]) {
    iconSetCache[prefix] = fetch(`https://api.iconify.design/collection?prefix=${prefix}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch icon set "${prefix}"`);
        return response.json();
      })
      .then((data) => {
        const names = [...Object.values(data.categories || {}).flat(), ...(data.uncategorized || [])];
        return { names: names.map((name) => `${prefix}:${name}`), error: '' };
      })
      .catch(() => {
        delete iconSetCache[prefix];
        return { names: [], error: `Failed to fetch "${prefix}" icons` };
      });
  }
  return iconSetCache[prefix];
}

/**
 * Fetches every icon name available in a given Iconify set (e.g. "fluent-emoji-flat").
 * @param {string} prefix - Iconify collection prefix.
 * @param {boolean} [enabled=true] - Set false to defer fetching (e.g. until a dialog opens).
 * @returns {[string[], boolean, string]} [icons, loading, error]
 */
export default function useIconSet(prefix, enabled = true) {
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) return undefined;
    let isMounted = true;
    setLoading(true);
    setError('');
    fetchIconSet(prefix).then(({ names, error: fetchError }) => {
      if (!isMounted) return;
      setIcons(names);
      setError(fetchError);
      setLoading(false);
    });
    return () => {
      isMounted = false;
    };
  }, [prefix, enabled]);

  return [icons, loading, error];
}
